import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";

/** Movement (px) before a press on the handle counts as a drag, not a tap. */
const DRAG_THRESHOLD_PX = 6;
/** How far ahead (ms) release velocity carries the sheet when snapping. */
const FLICK_PROJECTION_MS = 150;
/** A click this soon after a drag ends is the drag's own release. */
const POST_DRAG_CLICK_MS = 300;

export interface SheetSnap<K extends string> {
  key: K;
  px: number;
}

/**
 * Drag-to-resize for a bottom sheet. While dragging, `dragHeight` follows
 * the pointer (clamped to the snap range); on release the sheet snaps to the
 * snap point nearest where the release velocity would carry it. A press that
 * never moves past the threshold stays a tap: the click goes through.
 */
export function useSheetDrag<K extends string>({
  height,
  snaps,
  onSnap,
}: {
  /** Resting height in px. */
  height: number;
  snaps: SheetSnap<K>[];
  onSnap: (key: K) => void;
}) {
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const dragEndedAtRef = useRef(-Infinity);
  const stopRef = useRef<(() => void) | null>(null);

  useEffect(() => () => stopRef.current?.(), []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent) => {
      if (event.button !== 0 || snaps.length === 0) return;
      stopRef.current?.();
      // A new press means the last drag's own click has already come and gone
      dragEndedAtRef.current = -Infinity;

      const startY = event.clientY;
      const min = Math.min(...snaps.map((snap) => snap.px));
      const max = Math.max(...snaps.map((snap) => snap.px));
      let current = height;
      let lastY = startY;
      let lastTime = event.timeStamp;
      let velocity = 0; // px/ms, positive = growing
      let moved = false;

      const move = (e: PointerEvent) => {
        const delta = startY - e.clientY;
        if (!moved && Math.abs(delta) < DRAG_THRESHOLD_PX) return;
        moved = true;
        const elapsed = e.timeStamp - lastTime;
        if (elapsed > 0) velocity = (lastY - e.clientY) / elapsed;
        lastY = e.clientY;
        lastTime = e.timeStamp;
        current = Math.min(max, Math.max(min, height + delta));
        setDragHeight(current);
      };
      const stop = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", end);
        window.removeEventListener("pointercancel", end);
        stopRef.current = null;
      };
      const end = () => {
        stop();
        if (!moved) return;
        dragEndedAtRef.current = performance.now();
        const projected = current + velocity * FLICK_PROJECTION_MS;
        const target = snaps.reduce((best, snap) =>
          Math.abs(snap.px - projected) < Math.abs(best.px - projected)
            ? snap
            : best,
        );
        setDragHeight(null);
        onSnap(target.key);
      };

      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", end);
      window.addEventListener("pointercancel", end);
      stopRef.current = stop;
    },
    [height, snaps, onSnap],
  );

  /**
   * Swallows the click that ends a drag, so it isn't also a tap. Time-boxed,
   * because a drag released off the handle produces no click at all.
   */
  const onClickCapture = useCallback((event: MouseEvent) => {
    if (performance.now() - dragEndedAtRef.current > POST_DRAG_CLICK_MS) {
      return;
    }
    dragEndedAtRef.current = -Infinity;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return { dragHeight, onPointerDown, onClickCapture };
}
